import {
  Box,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';
import {
  FcAddRow,
  FcDeleteDatabase,
  FcDeleteRow,
  FcPlus,
} from 'react-icons/fc';
import { parseQuestions } from '@grcs/utils/content/parseQuestions';

export interface Question {
  question: string;
  choices: string[];
  answer: boolean[];
}

export interface IQuestionEditorProps {
  value: string | undefined;
  onChange?: (value: string) => void;
  editable?: boolean;
  showAnswer?: boolean;
}

export const QuestionEditor: React.FC<IQuestionEditorProps> = ({
  value,
  onChange = () => {},
  editable = true,
  showAnswer = false,
}) => {
  const questions: Question[] = parseQuestions(value || '[]');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const addQuestion = () => {
    onChange(
      JSON.stringify([
        ...questions,
        { question: '', choices: [''], answer: [false] },
      ]),
    );
  };

  const handleQuestionChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    questions[index].question = e.target.value;
    onChange(JSON.stringify(questions));
  };

  const removeQuestion = (questionIndex: number) => {
    questions.splice(questionIndex, 1);
    onChange(JSON.stringify(questions));
  };

  const handleChoiceChange = (
    questionIndex: number,
    choiceIndex: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    questions[questionIndex].choices[choiceIndex] = e.target.value;
    onChange(JSON.stringify(questions));
  };

  const addChoice = (questionIndex: number) => {
    questions[questionIndex].choices.push('');
    questions[questionIndex].answer.push(false);
    onChange(JSON.stringify(questions));
  };

  const removeChoice = (questionIndex: number, choiceIndex: number) => {
    questions[questionIndex].choices.splice(choiceIndex, 1);
    questions[questionIndex].answer.splice(choiceIndex, 1);
    onChange(JSON.stringify(questions));
  };

  const setAnswer = (
    questionIndex: number,
    choiceIndex: number,
    set: boolean,
  ) => {
    questions[questionIndex].answer[choiceIndex] = set;
    onChange(JSON.stringify(questions));
  };

  return (
    <Stack spacing={6}>
      {questions.map((question, questionIndex) => (
        <Box
          key={questionIndex}
          p={5}
          borderRadius="md"
          border="1px solid"
          borderColor={borderColor}
          bg={cardBg}
          boxShadow="sm"
        >
          <Stack spacing={4}>
            {/* Question Field */}
            <HStack align="flex-start" spacing={3}>
              <Input
                fontSize="sm"
                placeholder={`Question ${questionIndex + 1}`}
                value={question.question}
                onChange={(e) => handleQuestionChange(questionIndex, e)}
                flex={1}
              />
              {editable && (
                <IconButton
                  aria-label="Remove Question"
                  icon={<FcDeleteDatabase />}
                  onClick={() => removeQuestion(questionIndex)}
                  size="sm"
                  variant="ghost"
                />
              )}
            </HStack>

            {/* Choices */}
            <Stack spacing={3}>
              {question.choices.map((choice, choiceIndex) => (
                <HStack key={choiceIndex} spacing={3} align="center">
                  <Input
                    fontSize="sm"
                    placeholder={`Choice ${choiceIndex + 1}`}
                    value={choice}
                    onChange={(e) =>
                      handleChoiceChange(questionIndex, choiceIndex, e)
                    }
                    flex={1}
                  />
                  {editable ? (
                    <>
                      <Checkbox
                        isChecked={question.answer[choiceIndex]}
                        onChange={(e) =>
                          setAnswer(
                            questionIndex,
                            choiceIndex,
                            e.target.checked,
                          )
                        }
                      >
                        Answer
                      </Checkbox>
                      <IconButton
                        aria-label="Remove Choice"
                        icon={<FcDeleteRow />}
                        onClick={() => removeChoice(questionIndex, choiceIndex)}
                        size="sm"
                        variant="ghost"
                      />
                    </>
                  ) : (
                    <Checkbox
                      isChecked={question.answer[choiceIndex]}
                      isReadOnly
                    />
                  )}
                </HStack>
              ))}
            </Stack>

            {/* Add Choice CTA */}
            {editable && (
              <HStack pt={2}>
                <IconButton
                  aria-label="Add Choice"
                  icon={<FcAddRow />}
                  onClick={() => addChoice(questionIndex)}
                  variant="outline"
                  size="sm"
                  colorScheme="teal"
                />
                <Text fontSize="sm">Add Choice</Text>
              </HStack>
            )}
          </Stack>
        </Box>
      ))}

      {/* Add Question CTA */}
      {editable && (
        <HStack align="center">
          <IconButton
            aria-label="Add Question"
            icon={<FcPlus />}
            onClick={addQuestion}
            variant="solid"
            colorScheme="teal"
            size="sm"
          />
          <Text fontSize="sm">Add Question</Text>
        </HStack>
      )}
    </Stack>
  );
};
